/**
 * System-Modus — Master-Switch für die komplette Plattform.
 *
 * AN  : voice-agent läuft 24/7 warm (minScale=1), Scheduler aktiv,
 *       Webhooks beantworten Anrufe.
 * AUS : voice-agent fährt runter (minScale=0), Scheduler pausiert,
 *       Webhooks geben 503/parked zurück — kein GCP-Idle-Kosten.
 *
 * Soll bei laufenden Kunden NIE auf AUS stehen. Diese Seite ist
 * gedacht für die Testphase: Switch aus während dem Pausieren,
 * an wenn live.
 */
import * as React from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pause,
  Play,
  RefreshCw,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../components/ui/toast';
import {
  usePlatformRuntime,
  useSetPlatformRuntime,
} from '../../hooks/usePlatformRuntime';
import { cn } from '../../lib/utils';

export function PlatformRuntimePage() {
  const runtime = usePlatformRuntime();
  const setRuntime = useSetPlatformRuntime();
  const { toast } = useToast();
  const [confirming, setConfirming] = React.useState<'enable' | 'disable' | null>(null);

  const enabled = runtime.data?.enabled ?? true;
  const lastChangedAt = runtime.data?.lastChangedAt;
  const lastChangedEmail = runtime.data?.lastChangedEmail;
  const lastError = runtime.data?.lastError;

  async function applyChange(next: boolean) {
    setConfirming(null);
    try {
      const r = await setRuntime.mutateAsync(next);
      if (r.warnings.length > 0) {
        toast({
          title: next ? 'Plattform aktiviert (mit Warnungen)' : 'Plattform pausiert (mit Warnungen)',
          description: r.warnings.join(' · '),
          variant: 'destructive',
        });
      } else {
        toast({
          title: next ? 'Plattform aktiviert' : 'Plattform pausiert',
          description: next
            ? 'voice-agent skaliert hoch, Scheduler resumed.'
            : 'voice-agent minScale=0, Scheduler pausiert — keine Idle-Kosten mehr.',
        });
      }
    } catch (err) {
      toast({
        title: 'Umschalten fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="space-y-4">
      <Card
        className={cn(
          'border-2 transition-colors',
          enabled
            ? 'border-emerald-500/40 bg-emerald-500/5'
            : 'border-amber-500/40 bg-amber-500/5',
        )}
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap
              className={cn(
                'h-5 w-5',
                enabled ? 'text-emerald-400' : 'text-amber-400',
              )}
            />
            System-Modus
          </CardTitle>
          <CardDescription>
            Master-Switch für die komplette Plattform. AN für Live-Betrieb,
            AUS während Testphasen/Pausen um GCP-Idle-Kosten zu eliminieren.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Status banner */}
          <div className="flex items-start gap-4 rounded-md border border-border bg-card p-4">
            <div className="shrink-0">
              {runtime.isLoading ? (
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              ) : enabled ? (
                <CheckCircle2 className="h-8 w-8 text-emerald-400" />
              ) : (
                <Pause className="h-8 w-8 text-amber-400" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">
                  {enabled ? 'Plattform AKTIV' : 'Plattform PAUSIERT'}
                </span>
                {setRuntime.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {enabled
                  ? 'voice-agent läuft 24/7 warm · alle 7 Scheduler-Jobs aktiv · Webhooks beantworten Anrufe.'
                  : 'voice-agent skaliert auf 0 Instanzen · Scheduler pausiert · eingehende Anrufe werden mit 503 abgewiesen.'}
              </p>
              {lastChangedAt && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Zuletzt geändert{' '}
                  {new Date(lastChangedAt).toLocaleString('de-DE', {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                  {lastChangedEmail ? ` · von ${lastChangedEmail}` : ''}
                </p>
              )}
              {lastError && (
                <div className="mt-2 flex items-start gap-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Letzte Umschaltung hatte Warnungen: {lastError}
                  </span>
                </div>
              )}
            </div>
            <div className="shrink-0">
              <Switch
                checked={enabled}
                disabled={runtime.isLoading || setRuntime.isPending}
                onCheckedChange={(next) =>
                  setConfirming(next ? 'enable' : 'disable')
                }
                aria-label="Plattform an/aus"
              />
            </div>
          </div>

          {/* Confirmation */}
          {confirming && (
            <div className="rounded-md border border-border bg-card p-4">
              {confirming === 'disable' ? (
                <DisableConfirmation
                  onConfirm={() => void applyChange(false)}
                  onCancel={() => setConfirming(null)}
                  pending={setRuntime.isPending}
                />
              ) : (
                <EnableConfirmation
                  onConfirm={() => void applyChange(true)}
                  onCancel={() => setConfirming(null)}
                  pending={setRuntime.isPending}
                />
              )}
            </div>
          )}

          {/* Reload */}
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => runtime.refetch()}
              disabled={runtime.isFetching}
            >
              <RefreshCw
                className={cn(
                  'mr-1 h-3.5 w-3.5',
                  runtime.isFetching && 'animate-spin',
                )}
              />
              Status neu laden
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* What gets switched */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Was der Switch tatsächlich macht</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Row
            on="voice-agent läuft minScale=1 (2 GiB always-warm)"
            off="minScale=0 — Container shutdown nach ~5 min, Cold-Start bei nächster Aktivierung"
            savings="~€19 / Monat"
          />
          <Row
            on="7 Cloud Scheduler Jobs laufen (Notifications 1×/min, Eskalationen 15×/h, etc.)"
            off="Alle Jobs pausiert — kein Cron-Trigger, kein Compute"
            savings="kleinste Beträge + verhindert unnötige Compute-Spikes"
          />
          <Row
            on="Webhooks (Twilio/Telnyx/LiveKit) verarbeiten eingehende Anrufe normal"
            off="503/parked — Anrufer hört Carrier-Failover; keine LLM/STT/TTS-Kosten"
            savings="100 % der variablen Anruf-Kosten"
          />
          <Row
            on="Voice-Agent WebSocket-Endpoints akzeptieren Verbindungen"
            off="WS close 1013 — schon laufende Calls bleiben, neue werden abgelehnt"
            savings="—"
          />
        </CardContent>
      </Card>

      {/* Tips */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Wichtig zu wissen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Beim Wieder-Aktivieren:</strong>{' '}
            Der voice-agent braucht beim ERSTEN Anruf nach AUS→AN ca. 10-15 s
            Cold-Start. Danach läuft er wieder warm. Tipp: nach dem
            Aktivieren einmal kurz die Voice-Agent-Health-URL aufrufen
            (https://voice-agent-…run.app/health), damit der Container
            schon hochfährt bevor der erste echte Anruf reinkommt.
          </p>
          <p>
            <strong className="text-foreground">Während AUS:</strong>{' '}
            Bereits in den Cloud-Run-Logs sichtbar laufende Anrufe werden
            NICHT gekillt — sie laufen bis zum Hangup durch und der Container
            shutdownt dann normal. Nur NEUE eingehende Anrufe werden
            abgelehnt.
          </p>
          <p>
            <strong className="text-foreground">Kostenüberblick:</strong>{' '}
            Verbleibende Kosten im AUS-Zustand: Artifact-Registry-Storage
            (~€2.30/Monat für alte Container-Images), Logging-Storage
            (volumenabhängig), Firestore-Speicher. Insgesamt ≤ €3-5 / Monat
            statt der ~€22 / Monat im AN-Zustand.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  on,
  off,
  savings,
}: {
  on: string;
  off: string;
  savings: string;
}) {
  return (
    <div className="grid gap-2 rounded-md border border-border bg-muted/20 p-3 sm:grid-cols-[1fr_1fr_120px]">
      <div className="text-xs">
        <div className="mb-0.5 flex items-center gap-1 font-medium text-emerald-300">
          <Play className="size-3" /> AN
        </div>
        <span className="text-muted-foreground">{on}</span>
      </div>
      <div className="text-xs">
        <div className="mb-0.5 flex items-center gap-1 font-medium text-amber-300">
          <Pause className="size-3" /> AUS
        </div>
        <span className="text-muted-foreground">{off}</span>
      </div>
      <div className="text-right text-xs font-medium text-foreground">{savings}</div>
    </div>
  );
}

function DisableConfirmation({
  onConfirm,
  onCancel,
  pending,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  pending: boolean;
}) {
  return (
    <>
      <div className="mb-2 flex items-center gap-2 text-amber-300">
        <AlertTriangle className="size-4" />
        <span className="text-sm font-medium">Plattform wirklich pausieren?</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Alle eingehenden Anrufe werden ab sofort abgewiesen. voice-agent fährt
        innerhalb von ~5 Minuten runter. Scheduler-Jobs werden pausiert
        (Notifications, Eskalationen, Mahnungen). Aktive Anrufe laufen
        normal weiter bis zum Hangup.
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="destructive" onClick={onConfirm} disabled={pending}>
          {pending && <Loader2 className="mr-1 size-3.5 animate-spin" />}
          Ja, pausieren
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
          Abbrechen
        </Button>
      </div>
    </>
  );
}

function EnableConfirmation({
  onConfirm,
  onCancel,
  pending,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  pending: boolean;
}) {
  return (
    <>
      <div className="mb-2 flex items-center gap-2 text-emerald-300">
        <Play className="size-4" />
        <span className="text-sm font-medium">Plattform aktivieren</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        voice-agent skaliert auf minScale=1 hoch (Warm-Halten,
        ~€19/Monat Idle-Kosten ab jetzt), Scheduler werden resumed,
        Webhooks beantworten Anrufe wieder normal. Erster Anruf nach
        Aktivierung hat ~10-15 s Cold-Start.
      </p>
      <div className="flex gap-2">
        <Button size="sm" onClick={onConfirm} disabled={pending}>
          {pending && <Loader2 className="mr-1 size-3.5 animate-spin" />}
          Ja, aktivieren
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={pending}>
          Abbrechen
        </Button>
      </div>
    </>
  );
}
